import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { Protected } from "@/components/AppShell";
import { api } from "@/lib/api";
import { log } from "@/lib/logger";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, PaperPlaneTilt, ChartBar, ArrowRight, Stethoscope, PushPin, FileText, User, Sparkle, CaretRight, CheckCircle, ArrowClockwise, ChartLine } from "@phosphor-icons/react";
import { useAccessGate } from "@/hooks/useAccessGate";
import { toast } from "sonner";
import SendDialog from "@/components/SendDialog";
import PatientForm from "@/components/PatientForm";
import PatientAutocomplete from "@/components/PatientAutocomplete";
import OnboardingTour from "@/components/OnboardingTour";

// Readable trigger: allow wrapping on long titles + comfortable height.
const wrapTrigger =
  "bg-white text-[#1E3A5F] border-[#E1E9F0] rounded-[10px] min-h-[2.75rem] h-auto py-2 text-left " +
  "[&>span]:!line-clamp-2 [&>span]:whitespace-normal [&>span]:text-left [&>span]:leading-snug";

export default function Dashboard() {
  const { user } = useAuth();
  const isResearch = (user?.role || "admin") === "research";
  const [recent, setRecent] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [patients, setPatients] = useState([]);
  const [pickedTplId, setPickedTplId] = useState("");
  const [pickedPatientId, setPickedPatientId] = useState("");
  const [sendFor, setSendFor] = useState(null);
  const [patientFormOpen, setPatientFormOpen] = useState(false);
  const [directBusy, setDirectBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { isTrialExpired, gateProps } = useAccessGate();

  const load = async ({ silent = false } = {}) => {
    if (!silent) setRefreshing(true);
    try {
      // Research role has no send-workflow, so only the responses feed is
      // fetched — templates/patients dropdowns are absent from their view.
      const calls = isResearch
        ? [api.get("/responses")]
        : [api.get("/responses"), api.get("/templates"), api.get("/patient-records")];
      const results = await Promise.all(calls);
      setRecent(results[0].data.slice(0, 10));
      if (!isResearch) {
        setTemplates(results[1].data);
        setPatients(results[2].data);
      }
    } catch (err) {
      log.error("dashboard load failed", err);
      if (!silent) toast.error("Impossible de rafraîchir les données");
    } finally {
      if (!silent) setRefreshing(false);
    }
  };
  useEffect(() => { load({ silent: true }); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [isResearch]);

  const mine = templates
    .filter((t) => !t.is_validated)
    .sort((a, b) => {
      if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
      return (b.updated_at || "").localeCompare(a.updated_at || "");
    });
  const picked = templates.find((t) => t.template_id === pickedTplId);
  const canSend = !!pickedTplId && !!pickedPatientId;

  const launchSend = () => { if (picked) setSendFor(picked); };

  const launchDirectFill = async () => {
    if (!canSend || directBusy) return;
    setDirectBusy(true);
    try {
      const { data: res } = await api.post(`/templates/${pickedTplId}/direct-fill`, { patient_id: pickedPatientId });
      window.open(`/q/${res.token}`, "_blank", "noopener,noreferrer");
      toast.success("Questionnaire ouvert dans un nouvel onglet");
      setPickedTplId("");
      setPickedPatientId("");
      setTimeout(() => load(), 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir le questionnaire");
    } finally { setDirectBusy(false); }
  };

  const subTone = (interp) => {
    const s = (interp || "").toLowerCase();
    if (/sévère|severe|important|critique/.test(s)) return "crit";
    if (/modér|moder|léger|leger/.test(s)) return "warn";
    return "normal";
  };

  return (
    <Protected>
      <AppShell title="Accueil">
        {isResearch ? (
          /* Research-specific hero: no send workflow — the practitioner is
             the one dispatching questionnaires. Route the assistant straight
             to Études where their added value lives. */
          <div
            className="mb-10 rounded-[16px] border border-[#D6E3EC] bg-white p-7 md:p-8 shadow-[0_1px_0_rgba(30,58,95,0.03)]"
            data-testid="dashboard-research-hero"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-[12px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center shrink-0">
                <ChartLine size={22} weight="duotone" className="text-[#3A8FA5]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-[24px] md:text-[28px] leading-tight tracking-[-0.01em] font-bold text-[#1E3A5F] mb-1.5">
                  Bienvenue, {user?.name?.split(" ")[0] || "Assistant de recherche"}
                </h2>
                <p className="text-[15px] text-[#5F758F] leading-relaxed max-w-2xl">
                  Vous avez accès en lecture aux <strong className="text-[#1E3A5F]">questionnaires complétés</strong> du cabinet et à l'onglet <strong className="text-[#1E3A5F]">Études</strong> pour l'analyse statistique anonymisée des cohortes.
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  <Link to="/responses" data-testid="research-cta-responses">
                    <Button variant="outline" className="rounded-[8px] border-[#D6E3EC] text-[#1E3A5F] hover:border-[#1E3A5F] hover:bg-[#F4F7FB] font-semibold h-11 px-5">
                      Voir toutes les réponses
                    </Button>
                  </Link>
                  <Link to="/studies" data-testid="research-cta-studies">
                    <Button className="group rounded-[8px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold h-11 px-5">
                      Études
                      <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : (
        /* Hero — minimal send composer */
        <div
          className="mb-10 rounded-[16px] border border-[#D6E3EC] bg-white p-7 md:p-8 shadow-[0_1px_0_rgba(30,58,95,0.03)]"
          data-testid="dashboard-quicksend-card"
        >
          <h2 className="text-[24px] md:text-[28px] leading-tight tracking-[-0.01em] font-bold text-[#1E3A5F] mb-6">
            Envoyer un questionnaire
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <label className="text-[15px] font-semibold text-[#1E3A5F] flex items-center gap-1.5">
                Questionnaire
                {pickedTplId && <CheckCircle size={14} weight="fill" className="text-[#3A8FA5]" />}
              </label>
              <Select value={pickedTplId} onValueChange={setPickedTplId}>
                <SelectTrigger className={wrapTrigger} data-testid="quicksend-template">
                  <SelectValue placeholder="Sélectionner un modèle" />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-2rem)]">
                  {mine.length === 0 ? (
                    <div className="px-3 py-6 text-sm text-[#5F758F] text-center">
                      Aucun questionnaire créé.
                    </div>
                  ) : mine.map((t, idx) => {
                    const showSep = t.is_pinned === false && idx > 0 && mine[idx - 1]?.is_pinned;
                    return (
                      <div key={t.template_id}>
                        {showSep && <div className="my-1 border-t border-[#E1E9F0]" />}
                        <SelectItem value={t.template_id} className="whitespace-normal">
                          <span className="flex items-start gap-2 leading-snug">
                            {t.is_pinned && <PushPin size={13} weight="fill" className="text-[#3A8FA5] mt-0.5 shrink-0" />}
                            <span>{t.title}</span>
                          </span>
                        </SelectItem>
                      </div>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-[15px] font-semibold text-[#1E3A5F] flex items-center gap-1.5">
                Patient
                {pickedPatientId && <CheckCircle size={14} weight="fill" className="text-[#3A8FA5]" />}
              </label>
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <PatientAutocomplete
                    patients={patients}
                    value={pickedPatientId}
                    onChange={setPickedPatientId}
                    placeholder={patients.length === 0 ? "Aucun patient — créez-en un" : "Nom, prénom"}
                  />
                </div>
                <Button
                  variant="outline"
                  className="rounded-[8px] border-[#D6E3EC] text-[#1E3A5F] hover:border-[#1E3A5F] hover:bg-[#F4F7FB] shrink-0 h-11 px-4 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="quicksend-add-patient"
                  {...gateProps({ onClick: () => setPatientFormOpen(true), title: "Créer un patient" })}
                >
                  <Plus size={15} className="mr-1.5" />
                  Créer
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row sm:justify-end gap-2">
            <Button
              disabled={!canSend || directBusy || isTrialExpired}
              variant="outline"
              className="rounded-[8px] border-[#3A8FA5] text-[#3A8FA5] hover:bg-[#3A8FA5] hover:text-white font-semibold h-12 px-6 text-[15px] disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="quicksend-direct-fill-btn"
              {...gateProps({ onClick: launchDirectFill, title: "Remplir le questionnaire maintenant, avec le patient en consultation" })}
            >
              <Stethoscope size={16} className="mr-2" />
              {directBusy ? "Ouverture…" : "Remplir en consultation"}
            </Button>
            <Button
              disabled={!canSend || isTrialExpired}
              className="group rounded-[8px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold h-12 px-7 text-[15px] disabled:opacity-30 disabled:cursor-not-allowed"
              data-testid="quicksend-btn"
              {...gateProps({ onClick: launchSend })}
            >
              Envoyer au patient
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-0.5" />
            </Button>
          </div>
        </div>
        )}

        {/* Dernières réponses */}
        <div className="flex items-end justify-between gap-3 mb-5">
          <div>
            <h3 className="font-serif text-[22px] md:text-[24px] leading-tight tracking-tight text-[#1E3A5F] flex items-center gap-3">
              Dernières réponses
              {recent.length > 0 && (
                <span className="bg-[#1E3A5F] text-white text-[12px] px-2 py-0.5 rounded-full font-bold tabular-nums leading-none pt-1">{recent.length}</span>
              )}
            </h3>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => load()}
              disabled={refreshing}
              className="group inline-flex items-center gap-1.5 text-[13px] text-[#5F758F] hover:text-[#1E3A5F] font-semibold transition-colors disabled:opacity-50 disabled:cursor-wait"
              data-testid="dashboard-refresh-btn"
              title="Rafraîchir"
            >
              <ArrowClockwise
                size={14}
                weight="bold"
                className={refreshing ? "animate-spin" : "transition-transform group-hover:rotate-45"}
              />
              Rafraîchir
            </button>
            <span className="w-px h-4 bg-[#E1E9F0]" aria-hidden="true" />
            <Link to="/responses" className="group text-[13px] text-[#3A8FA5] hover:text-[#1E3A5F] font-semibold inline-flex items-center gap-1 transition-colors" data-testid="dashboard-see-all-responses">
              Tout voir <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>

        {recent.length === 0 ? (
          <div className="rounded-[20px] border border-dashed border-[#C7DEE6] bg-[#F8FBFC] p-12 text-center">
            <div className="w-12 h-12 rounded-full bg-white border border-[#E1E9F0] flex items-center justify-center mx-auto mb-4">
              <ChartBar size={20} weight="duotone" className="text-[#3A8FA5]" />
            </div>
            <p className="text-[#5F758F] mb-5 leading-relaxed">
              Aucune réponse pour le moment.
              {!isResearch && (
                <><br /><span className="text-[13px]">Envoyez votre premier questionnaire ou piochez dans la bibliothèque.</span></>
              )}
              {isResearch && (
                <><br /><span className="text-[13px]">Dès que le praticien recevra des réponses de ses patients, elles apparaîtront ici.</span></>
              )}
            </p>
            {!isResearch && (
              <Link to="/templates?tab=library" data-testid="empty-library-link">
                <Button variant="outline" className="rounded-[10px] border-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white font-semibold">
                  Découvrir la bibliothèque
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {recent.map((r) => {
              const tone = subTone(r.score?.interpretation);
              const toneClasses = tone === "crit"
                ? { chip: "bg-[#FFE0DC] text-[#8B2A1E] border-[#EEC1BB]", score: "text-[#8B2A1E]", avatar: "bg-[#FFE0DC] text-[#8B2A1E]" }
                : tone === "warn"
                  ? { chip: "bg-[#FFF3D6] text-[#8A5A00] border-[#F0DDA8]", score: "text-[#8A5A00]", avatar: "bg-[#FFF3D6] text-[#8A5A00]" }
                  : { chip: "bg-[#E6F1F5] text-[#164E5F] border-[#C7DEE6]", score: "text-[#164E5F]", avatar: "bg-[#EAF1F5] text-[#3A8FA5]" };
              const initials = (r.patient_name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("") || "?";
              const dt = new Date(r.submitted_at);
              const dateStr = dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
              const timeStr = dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
              return (
                <Link
                  key={r.response_id}
                  to={`/responses/${r.response_id}`}
                  className="group relative bg-white border border-[#E1E9F0] rounded-[18px] px-5 py-4 md:px-6 md:py-5 grid grid-cols-[auto_1fr_auto] gap-4 md:gap-5 items-center transition-all hover:border-[#3A8FA5]/60 hover:shadow-[0_1px_2px_rgba(30,58,95,0.04),0_10px_24px_-14px_rgba(30,58,95,0.25)] hover:-translate-y-0.5"
                  data-testid={`response-${r.response_id}`}
                >
                  {/* Avatar initials */}
                  <span className={`w-11 h-11 rounded-full flex items-center justify-center text-[13px] font-bold tracking-wide shrink-0 ${toneClasses.avatar}`} aria-hidden>
                    {initials}
                  </span>
                  {/* Patient + template + date */}
                  <div className="min-w-0">
                    <p className="text-[15.5px] font-semibold text-[#1E3A5F] truncate">{r.patient_name}</p>
                    <p className="text-[13px] text-[#5F758F] mt-0.5 truncate">
                      <span className="text-[#1E3A5F]/70">{r.template_title}</span>
                      <span className="mx-1.5 text-[#B7C4D6]">·</span>
                      {dateStr}
                      <span className="mx-1 text-[#B7C4D6]">·</span>
                      <span className="tabular-nums">{timeStr}</span>
                    </p>
                  </div>
                  {/* Score cluster */}
                  {r.score ? (
                    <div className="flex items-center gap-3 shrink-0">
                      {r.score.interpretation && (
                        <span className={`hidden sm:inline text-[11.5px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap border ${toneClasses.chip}`}>
                          {r.score.interpretation}
                        </span>
                      )}
                      <div className="text-right">
                        <p className="text-[10px] tracking-[0.14em] uppercase text-[#5F758F] font-bold leading-none mb-1.5">Score</p>
                        <p className={`text-[22px] font-bold leading-none tracking-tight tabular-nums ${toneClasses.score}`}>
                          {r.score.percentage != null ? `${r.score.percentage} %` : `${r.score.total} / ${r.score.max}`}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </Link>
              );
            })}
            {recent.length >= 10 && (
              <Link
                to="/responses"
                className="group mt-1 flex items-center justify-center gap-2 rounded-[14px] border border-dashed border-[#C7DEE6] bg-white px-5 py-3.5 text-[13.5px] font-semibold text-[#3A8FA5] transition-colors hover:border-[#3A8FA5] hover:bg-[#E6F1F5] hover:text-[#1E3A5F]"
                data-testid="dashboard-see-more-responses"
              >
                Voir toutes les réponses
                <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
              </Link>
            )}
          </div>
        )}

        <SendDialog
          template={sendFor}
          presetPatientId={pickedPatientId}
          onClose={() => { setSendFor(null); setPickedTplId(""); setPickedPatientId(""); load(); }}
        />
        <PatientForm
          open={patientFormOpen}
          initial={null}
          onClose={() => setPatientFormOpen(false)}
          onSaved={(p) => { setPatientFormOpen(false); load(); setPickedPatientId(p.patient_id); }}
        />
        <OnboardingTour />
      </AppShell>
    </Protected>
  );
}
